import { useEffect, useRef, useSyncExternalStore } from 'react';
import { voicePerformance } from '@elena/shared';
import {
  attachAvatar,
  avatarStatus,
  getAvatarSnapshot,
  lookAtPointer,
  setAvatarMode,
  webglAvailable,
} from '../lib/avatar3d';
import { AvatarBoundary } from './AvatarBoundary';
import { ElenaPhoto, type ElenaAvatarProps } from './ElenaPhoto';

const canRender3d = typeof window !== 'undefined' && webglAvailable();

/** Elena v3: the live 3D character, with the v2 photo when WebGL is missing or fails. */
export function ElenaAvatar(props: ElenaAvatarProps) {
  const avatar = useSyncExternalStore(avatarStatus.subscribe, getAvatarSnapshot);
  if (!canRender3d || avatar.status === 'failed') return <ElenaPhoto {...props} />;
  return (
    <AvatarBoundary fallback={<ElenaPhoto {...props} />}>
      <Elena3d {...props} />
    </AvatarBoundary>
  );
}

function Elena3d({ mode, size = 'hero', label }: ElenaAvatarProps) {
  const mount = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const avatar = useSyncExternalStore(avatarStatus.subscribe, getAvatarSnapshot);
  const performance = useSyncExternalStore(
    voicePerformance.subscribe,
    voicePerformance.getSnapshot,
  );
  const active =
    mode === 'listening' || mode === 'thinking' ? mode : performance.speaking ? 'speaking' : 'idle';

  useEffect(() => {
    if (!mount.current) return;
    return attachAvatar(mount.current, size === 'compact' ? 'head' : 'upper');
  }, [size]);

  useEffect(() => {
    setAvatarMode(active);
  }, [active, avatar.status]);

  useEffect(() => {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    let last = 0;
    const look = (e: PointerEvent) => {
      const now = window.performance.now();
      if (now - last < 450) return;
      last = now;
      lookAtPointer(e.clientX, e.clientY);
    };
    window.addEventListener('pointermove', look, { passive: true });
    return () => window.removeEventListener('pointermove', look);
  }, []);

  // The halo around her breathes with her real voice (PCM energy from lib/speech.ts).
  useEffect(() => {
    root.current?.style.setProperty(
      '--voice',
      String(performance.speaking ? performance.energy : 0),
    );
  }, [performance]);

  const status =
    label ??
    (active === 'listening'
      ? 'Slušam te'
      : active === 'thinking'
        ? 'Razmišljam…'
        : active === 'speaking'
          ? 'Pričam ti'
          : 'Tu sam za tebe');

  return (
    <div
      ref={root}
      className={`elena-avatar elena-3d is-${active} is-${size} is-${avatar.status}`}
      role="img"
      aria-label={`Elena — ${status}`}
    >
      <div className="elena-stage" aria-hidden="true">
        <span className="elena-halo" />
        <div ref={mount} className="elena3d-mount" />
        {avatar.status !== 'ready' && (
          <span className="elena3d-loading">
            <b style={{ transform: `scaleX(${avatar.progress})` }} />
          </span>
        )}
        <span className="elena-orbit" />
        <span className="elena-corner elena-corner-tl" />
        <span className="elena-corner elena-corner-br" />
      </div>
      <span className="elena-status">
        <i />
        {status}
        <span className="elena-mini-wave" aria-hidden="true">
          <b />
          <b />
          <b />
          <b />
        </span>
      </span>
    </div>
  );
}
